import React from 'react';
import { connect } from 'react-redux';
import { withCookies } from 'react-cookie';
import _ from 'lodash';
import {ButtonBase, IconButton, Tooltip} from '@mui/material';
import {Screen, Input, Button, Select, Checkbox} from '../components';
import {setUser} from '../actions';
import {
    __,
    toNumber,
    isEmptyString,
    request,
    formatAmount,
    getCurrency,
    getAiModels,
    getAiFileTools,
} from '../functions';
import '../assets/styles/ai-function.css';
import {AI_FUNCTION_DESCRIPTIONS, AI_FUNCTION_NAMES, AI_TYPES, FILES_NAMES, AI_TYPE_SETTINGS, AI_EXTENSIONS_NOT_FILE_SEARCH} from "../config";
import {Slider} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

/**
 * AI function.
 */
class AiFunctionScreen extends Screen {
    title = __('AI funkcia');

    state = {
        data: {},
        id: '',
        name: '',
        settings: {},
        files: {},
        loading: false,
        type: '',
        addon_search: false,
    };

    async componentDidMount() {
        if (super.componentDidMount()) {
            const { params, setUser } = this.props;

            request(`/ai-functions/view/${params.id}`).then(response => {
                const { status, data, sync } = response.data;

                if (status === 'error') {
                    window.location = '/ai-functions';
                    return;
                }

                setUser(sync.user);

                let settings = data.settings;
                let type = '';

                _.each(AI_TYPE_SETTINGS, ({ temperature, top_p }, key) => {
                    if (temperature === settings.temperature && top_p === settings.top_p) {
                        // Has predefined setting
                        type = key;
                    }
                });

                let files = {};

                _.each(data.function_files, ({ file_id, tool }) => {
                    files = { ...files, [file_id]: tool };
                });

                this.setState({
                    id: params.id,
                    data,
                    name: data.def && _.has(AI_FUNCTION_NAMES, data.name) ? AI_FUNCTION_NAMES[data.name] : data.name,
                    settings,
                    files,
                    type,
                });
            });
        }

        return true;
    }

    save() {
        let { id, name, settings, files, data } = this.state;

        if (this.isDemo()) {
            this.showSnackbar('error', __('Funkciu nie je možné uložiť na DEMO účte'));
            return;
        }

        let error = '';

        if (isEmptyString(name)) {
            error = __('Nie je vyplnený názov');
        } else if (!data.def && isEmptyString(settings.instructions)) {
            error = __('Nie sú vyplnené inštrukcie');
        }

        if (error !== '') {
            // Mame error
            this.showSnackbar('error', error);
            return;
        }

        this.setState({ loading: true });

        if (toNumber(id) > 0) {
            request(`/ai-functions/edit/${id}`, { name, settings, files }, 'POST').then(response => {
                const { status } = response.data;

                this.setState({ loading: false });

                if (status === 'error') {
                    this.showSnackbar('error', __('Nepodarilo sa uložiť funkciu'));
                    return;
                }

                this.showSnackbar('success', __('Funkcia bola uložená'));

                setTimeout(() => { window.location = `/ai-functions/view/${id}`; }, 500);
            });

            return;
        }

        request('/ai-functions/create', { name, settings, files }, 'POST').then(response => {
            const { data, status } = response.data;

            this.setState({ loading: false });

            if (status === 'error') {
                this.showSnackbar('error', __('Nepodarilo sa vytvoriť funkciu'));
                return;
            }

            this.showSnackbar('success', __('Funkcia bola vytvorená'));

            setTimeout(() => { window.location = `/ai-functions/view/${data.id}`; }, 500);
        });
    }

    onChangeType(type) {
        const { settings } = this.state;

        if (type === '') {
            this.setState({ type });
            return;
        }

        this.setState({ type, settings: { ...settings, ...AI_TYPE_SETTINGS[type] } });
    }

    onChangeName(name) {
        this.setState({ name });
    }

    onChangeSetting(type, value) {
        const { data, settings } = this.state;

        switch (type) {
            case 'max_tokens':
                if (value !== '') {
                    const max = toNumber(data.max_tokens);

                    value = toNumber(value);
                    value = value < 1 ? 1 : value;
                    value = value >= max ? max : value;
                    value = value.toString();
                }
                break;
        }

        let additional = {};

        this.setState({ settings: { ...settings, [type]: value, ...additional } });
    }

    onChangeSearchAddon(type, value) {
        const { data, settings } = this.state;

        switch (type) {
            case 'limit':
                if (value !== '') {
                    const max = toNumber(data.max_search_limit);

                    value = toNumber(value);
                    value = value < 1 ? 1 : value;
                    value = value >= max ? max : value;
                    value = value.toString();
                }
                break;
        }

        this.onChangeSetting('search', { ...settings.search, [type]: value });
    }

    onActiveSearchAddon(value) {
        this.setState({ addon_search: value });

        if (!value) {
            // Clear query
            this.onChangeSearchAddon('query', '');
        }
    }

    notAllowFileSearch(id) {
        const { data } = this.state;

        return _.has(data.file_extensions, id) && _.includes(AI_EXTENSIONS_NOT_FILE_SEARCH, data.file_extensions[id]);
    }

    addFile(id) {
        const { data, files } = this.state;

        let file_tool = data.default_file_tool;

        if (this.notAllowFileSearch(id)) {
            file_tool = 'code_interpreter';
        }

        this.setState({ files: { ...files, [id]: file_tool } });
    }

    onChangeFile(id, tool) {
        const { files } = this.state;

        this.setState({ files: { ...files, [id]: tool } });
    }

    removeFile(id) {
        let { files } = this.state;

        files = _.omit(files, [id]);

        this.setState({ files });
    }

    /**
     * Rendrovanie.
     *
     * @returns {JSX.Element}
     */
    render() {
        const { id, data, loading, name, settings, type, files, addon_search } = this.state;

        if (_.isEmpty(data)) {
            // Data nie su nacitane
            return this.renderLoading();
        }

        const isEdit = toNumber(id) > 0;

        if (isEdit && !this.hasPermission('aifunctions-edit')) {
            // Nema pravo
            return null;
        } else if (!isEdit && !this.hasPermission('aifunctions-create')) {
            // Nema pravo
            return null;
        }

        const isDefault = data.def;
        let description = settings.description;

        if (isDefault && _.has(AI_FUNCTION_DESCRIPTIONS, description)) {
            description = AI_FUNCTION_DESCRIPTIONS[description];
        }

        // Remove selected files
        const allFiles = _.omit(data.files, _.keys(files));

        const models = getAiModels(data.models);
        const fileTools = getAiFileTools(data.file_tools);

        return (
            <div className="ai-function">
                <div className="ai-function__header">
                    <div className="ai-function__header__left">
                        <div className="ai-function__header__left__title">
                            {`${isEdit ? _.truncate(name, { length: 64 }) : __('Nová AI funkcia')}`}
                        </div>
                    </div>
                    <div className="ai-function__header__right">
                        <Button
                            onClick={() => window.location = '/ai-functions'}
                            color="shadow"
                        >{__('Naspäť')}</Button>
                    </div>
                </div>
                <div className="ai-function__content">
                    <div className="ai-function__content__panels">
                        <div className="ai-function__content__panels__panel">
                            <div className="ai-function__content__panels__panel__title">{__('Nastavenia')}</div>
                            <Input
                                label={__('Názov')}
                                value={name}
                                onChange={!isDefault ? value => this.onChangeName(value) : () => {}}
                            />
                            <Input
                                label={__('Popis')}
                                value={description}
                                onChange={!isDefault ? value => this.onChangeSetting('description', value) : () => {}}
                                multiline
                            />
                            <Input
                                label={__('Maximálny počet tokenov pre výstup')}
                                value={settings.max_tokens}
                                onChange={value => this.onChangeSetting('max_tokens', value)}
                            />
                            <div className="ai-function__content__panels__panel__info">{__('Čím väčší počet, tým je odpoveď od AI dlhšia, komplexnejšia a drahšia. Maximálne {COUNT}.').replace('{COUNT}', data.max_tokens)}</div>
                        </div>
                        <div className="ai-function__content__panels__panel">
                            <div className="ai-function__content__panels__panel__title">{__('Podklady')}</div>
                            <Input
                                label={__('Inštrukcie')}
                                value={settings.instructions}
                                onChange={value => this.onChangeSetting('instructions', value)}
                                multiline
                            />
                            <div className="ai-function__content__panels__panel__info">{__('Vaše doplňujúce inštrukcie pre AI. Zadávate všeobecné inštrukcie bez vstupných dát, vstupné dáta zadávate keď zavoláte túto funkciu. Vstupné dáta sa pripoja na koniec inštrukcií.')}</div>
                            <Select
                                label={__('Model')}
                                value={settings.model}
                                options={models}
                                onChange={value => this.onChangeSetting('model', value)}
                                allowEmpty={false}
                            />
                            <div className="ai-function__content__panels__panel__info">{__('Voľba modelu ovplyvňuje kvalitu výstupov aj výslednu cenu.')}</div>
                            {type !== '' ? <Select
                                label={__('Osobnosť')}
                                value={type}
                                options={AI_TYPES}
                                onChange={value => this.onChangeType(value)}
                                allowEmpty={false}
                            /> : null}
                            {type !== '' ? <div className="ai-function__content__panels__panel__info">{__('Osobnosť ktorú určíte AI. Na základe osobnosti sa menia aj výstupy.')}</div> : null}
                            {type !== '' ? <ButtonBase
                                onClick={() => this.onChangeType('')}
                                className="ai-function__content__panels__panel__button-settings"
                            >{__('Zadať vlastnú osobnosť')}</ButtonBase> : null}
                            {type === '' ? <div className="input">
                                <div className="input__label slider__label">Temperature</div>
                                <Slider
                                    onChange={(event, value) => this.onChangeSetting('temperature', value)}
                                    value={settings.temperature}
                                    valueLabelDisplay="auto"
                                    min={0}
                                    max={2}
                                    step={0.05}
                                />
                            </div> : null}
                            {type === '' ? <div className="ai-function__content__panels__panel__info">{__('Ovláda kreativitu odpovedí. Nižšia hodnota = konzistentnejšie odpovede, vyššia = kreatívnejšie.')}</div> : null}
                            {type === '' ? <div className="input">
                                <div className="input__label slider__label">{__('Rozmanitosť výstupu')}</div>
                                <Slider
                                    onChange={(event, value) => this.onChangeSetting('top_p', value)}
                                    value={settings.top_p}
                                    valueLabelDisplay="auto"
                                    min={0}
                                    max={1}
                                    step={0.05}
                                />
                            </div> : null}
                            {type === '' ? <div className="ai-function__content__panels__panel__info">{__('Alternatíva ku temperature, ovláda diverzitu výstupu.')}</div> : null}
                            <div className="ai-function__content__panels__panel__addons">
                                <div className="ai-function__content__panels__panel__title">{__('Doplnky')}</div>
                                <div className="ai-function__content__panels__panel__addons__item">
                                    <div className="ai-function__content__panels__panel__addons__item__checkbox">
                                        <Checkbox
                                            value={addon_search || settings.search.query !== ''}
                                            onChange={checked => this.onActiveSearchAddon(checked)}
                                        />
                                    </div>
                                    <div className="ai-function__content__panels__panel__addons__item__info">
                                        <div className="ai-function__content__panels__panel__addons__item__info__header">
                                            <div className="ai-function__content__panels__panel__addons__item__info__header__name">{__('Google vyhľadávanie')}</div>
                                            <div className="ai-function__content__panels__panel__addons__item__info__header__price">+{formatAmount(data.addon_search_price, 'EUR', 8)}</div>
                                        </div>
                                        <div className="ai-function__content__panels__panel__addons__item__info__text">{__('Vyhľadá aktuálne výsledky cez Google a poskytne ich AI. ')}</div>
                                        {addon_search || settings.search.query !== '' ? <div className="ai-function__content__panels__panel__addons__item__info__settings">
                                            <Input
                                                label={__('Dotaz na vyhľadávanie')}
                                                value={settings.search.query}
                                                onChange={value => this.onChangeSearchAddon('query', value)}
                                            />
                                            <div className="ai-function__content__panels__panel__info">{__('Vo vyhľadávacom dotaze môžete použiť špeciálne slovo {input} za ktoré sa nahradí vstup pri volaní funkcie.')}</div>
                                            <Input
                                                label={__('Limit vrátených výsledkov')}
                                                value={settings.search.limit}
                                                onChange={value => this.onChangeSearchAddon('limit', value)}
                                            />
                                            <div className="ai-function__content__panels__panel__info">{__('Limit koľko výsledkov sa má vyhľadať. Ovplyvňuje aj koncovú cenu vstupu pre AI, čím viac vyhľadaných stránok tym viac príloh pre AI. Maximálny počet výsledkov je {COUNT}.').replace('{COUNT}', data.max_search_limit)}</div>
                                        </div> : null}
                                    </div>
                                </div>
                            </div>
                            <div className="ai-function__content__panels__panel__files">
                                <div className="ai-function__content__panels__panel__title">{__('Prílohy')}</div>
                                <div className="ai-function__content__panels__panel__files__list">
                                    {_.map(files, (file_tool, file_id) => {
                                        if (!_.has(data.files, file_id)) {
                                            return null;
                                        }

                                        let extension = null;
                                        let price = null;
                                        let def = 0;

                                        _.each(data.function_files, function_file => {
                                            if (toNumber(function_file.file_id) === toNumber(file_id)) {
                                                extension = function_file._matchingData.Files.extension;
                                                price = function_file.price;
                                                def = function_file.def;
                                            }
                                        });

                                        const isDefault = toNumber(def) > 0;
                                        const file_name = isDefault && _.has(FILES_NAMES, data.files[file_id]) ? FILES_NAMES[data.files[file_id]] : data.files[file_id];

                                        return (
                                            <div className="ai-function__content__panels__panel__files__list__item" key={file_id}>
                                                <div className="ai-function__content__panels__panel__files__list__item__left">
                                                    <div className="ai-function__content__panels__panel__files__list__item__left__name">{_.truncate(file_name, { length: 16 })}</div>
                                                    {extension !== null ? <div className="ai-function__content__panels__panel__files__list__item__left__extension">{extension}</div> : null}
                                                    <Select
                                                        value={file_tool}
                                                        options={fileTools}
                                                        onChange={value => this.onChangeFile(file_id, value)}
                                                        allowEmpty={false}
                                                        disabled={this.notAllowFileSearch(file_id)}
                                                    />
                                                </div>
                                                <div className="ai-function__content__panels__panel__files__list__item__right">
                                                    {price !== null ? <div className="ai-function__content__panels__panel__files__list__item__right__price">
                                                        <span>{__('Cena')}:</span>
                                                        <span>{formatAmount(price, 'EUR', 8)}</span>
                                                    </div> : null}
                                                    <Tooltip title={__('Zmazať')}>
                                                        <IconButton onClick={() => this.removeFile(file_id)} disabled={isDefault}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <Select
                                    label={__('Pridať prílohu z úložiska')}
                                    options={allFiles}
                                    onChange={value => this.addFile(value)}
                                />
                            </div>
                        </div>
                    </div>
                    <Button
                        onClick={() => this.save()}
                        loading={loading}
                        className="ai-function__content__button"
                        color="green"
                    >{__('Uložiť')}</Button>
                </div>
                {this.renderSnackbar()}
            </div>
        );
    }
}

const stateToProps = ({ user }) => ({ user });

export default withCookies(connect(stateToProps, {
    setUser,
})(AiFunctionScreen));
